/**
 * Back in stock via Klaviyo.
 *
 * Två anrop, båda klientsidiga:
 *   1. /client/profiles/  - sätter locale och market som profilegenskaper.
 *   2. /client/back-in-stock-subscriptions/ - själva bevakningen (svarar 202).
 *
 * Ändrad 2026-10-13: variant-ID och varianttitel hämtas nu från produktformuläret
 * för just denna sektion (product-form-<section-id>) i stället för att söka efter
 * närmaste product-info. Popupen ligger utanför product-info, och den gamla
 * sökningen föll då tillbaka på hela dokumentet och kunde träffa fel element,
 * vilket gav fel storlek i formuläret (och potentiellt fel bevakad variant).
 *
 * Titeln slås upp i en variantkarta (id -> titel) utifrån SAMMA variant-ID som
 * skickas till Klaviyo, så visad storlek och bevakad storlek kan aldrig skilja sig.
 * Allt läses färskt vid öppning och vid submit - inga sparade DOM-referenser.
 *
 * Dokumentation: https://developers.klaviyo.com/en/docs/how_to_set_up_custom_back_in_stock
 */
(function () {
  'use strict';

  var BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var PROFILE_ENDPOINT = 'https://a.klaviyo.com/client/profiles/';
  var REVISION = '2024-06-15';

  if (customElements.get('klaviyo-bis')) return;

  customElements.define(
    'klaviyo-bis',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-notification-form]');
        this.email = this.querySelector('[data-notification-email]');
        this.button = this.querySelector('[data-notification-submit]');
        this.fields = this.querySelector('[data-notification-fields]');
        this.message = this.querySelector('[data-notification-message]');
        this.messageText = this.querySelector('[data-notification-message-text]');
        this.messageVariant = this.querySelector('[data-notification-message-variant]');
        this.variantLabel = this.querySelector('[data-variant-title]');

        this.variantMap = {};
        var mapEl = this.querySelector('script[data-bis-variants]');
        if (mapEl) {
          try {
            this.variantMap = JSON.parse(mapEl.textContent) || {};
          } catch (e) {
            this.variantMap = {};
          }
        }

        if (!this.form) return;

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.sync();
        this.watch();
      }

      get sectionId() {
        return this.dataset.sectionId || '';
      }

      /** Produktformuläret för just denna sektion. */
      get productForm() {
        if (!this.sectionId) return null;
        return document.getElementById('product-form-' + this.sectionId);
      }

      /** Variantväljaren för just denna sektion (läses färskt varje gång). */
      get variantSelects() {
        if (this.sectionId) {
          var el = document.getElementById('variant-selects-' + this.sectionId);
          if (el) return el;
        }
        var root = this.closest('product-info');
        return root ? root.querySelector('variant-selects') : null;
      }

      watch() {
        var self = this;
        var resync = function () {
          self.sync();
          setTimeout(function () { self.sync(); }, 100);
          setTimeout(function () { self.sync(); }, 400);
        };

        // Popup/dialog öppnas
        var dialog = this.closest('dialog');
        if (dialog && typeof MutationObserver !== 'undefined') {
          new MutationObserver(function () {
            if (dialog.hasAttribute('open')) resync();
          }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
        }
        var popup = this.closest('popup-component');
        if (popup) popup.addEventListener('click', resync);

        // Storleksbyte var som helst på sidan (delegerat - överlever att temat
        // byter ut variantväljarens DOM)
        document.addEventListener('change', function (event) {
          var target = event.target;
          if (!target || !target.closest) return;
          if (target.closest('variant-selects') || target.matches('input[name="id"]')) resync();
        });
        document.addEventListener('click', function (event) {
          var target = event.target;
          if (target && target.closest && target.closest('variant-selects')) resync();
        });

        window.addEventListener('popstate', resync);
      }

      /** Variant-ID från denna sektions produktformulär. */
      get variantId() {
        var form = this.productForm;
        var input = form ? form.querySelector('input[name="id"]') : null;
        if (!input && this.sectionId) {
          input = document.querySelector('input[name="id"][form="product-form-' + this.sectionId + '"]');
        }
        if (input && input.value) return String(input.value);

        try {
          var fromUrl = new URLSearchParams(window.location.search).get('variant');
          if (fromUrl && this.variantMap[fromUrl]) return fromUrl;
        } catch (e) {
          /* ignorera */
        }

        return this.dataset.variantId;
      }

      /** Titeln hämtas för SAMMA ID som skickas. Reserv: variantväljarens etiketter. */
      get variantTitle() {
        var id = this.variantId;
        if (id && this.variantMap[id]) return this.variantMap[id];

        var selects = this.variantSelects;
        if (selects) {
          var values = Array.prototype.slice
            .call(selects.querySelectorAll('[data-selected-value]'))
            .map(function (el) { return (el.textContent || '').trim(); })
            .filter(Boolean);
          if (values.length) return values.join(' / ');
        }
        return this.variantLabel ? this.variantLabel.textContent.trim() : '';
      }

      sync() {
        if (!this.variantLabel) return;
        var title = this.variantTitle;
        if (title && this.variantLabel.textContent.trim() !== title) {
          this.variantLabel.textContent = title;
        }
      }

      get profileProperties() {
        var props = {};
        if (this.dataset.locale) props.locale = this.dataset.locale;
        if (this.dataset.market) props.market = this.dataset.market;
        return props;
      }

      headers() {
        return { 'content-type': 'application/json', revision: REVISION };
      }

      saveProfileProperties(email, companyId) {
        var props = this.profileProperties;
        if (!Object.keys(props).length) return Promise.resolve();

        return fetch(PROFILE_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify({
            data: { type: 'profile', attributes: { email: email, properties: props } }
          })
        })
          .then(function (response) {
            if (response.status >= 400) {
              response.text().then(function (body) {
                console.warn('[Back in stock] Kunde inte spara profilegenskaper', response.status, body);
              });
            }
          })
          .catch(function (err) {
            console.warn('[Back in stock] Nätverksfel vid profilegenskaper', err);
          });
      }

      onSubmit(event) {
        event.preventDefault();

        if (!this.email || !this.email.checkValidity()) {
          if (this.email) this.email.reportValidity();
          return;
        }

        this.sync();
        var variantId = this.variantId;
        var variantTitle = this.variantTitle;
        var companyId = this.dataset.companyId;
        var email = this.email.value.trim();

        if (!variantId || !companyId) {
          this.showMessage(this.dataset.errorText, false, '');
          return;
        }

        this.setBusy(true);
        var self = this;

        this.saveProfileProperties(email, companyId).then(function () {
          return fetch(BIS_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
            method: 'POST',
            headers: self.headers(),
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  profile: { data: { type: 'profile', attributes: { email: email } } },
                  channels: ['EMAIL']
                },
                relationships: {
                  variant: {
                    data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId }
                  }
                }
              }
            })
          })
            .then(function (response) {
              self.setBusy(false);
              if (response.status === 202) {
                self.showMessage(self.dataset.successText, true, variantTitle);
                return;
              }
              response.text().then(function (body) {
                console.warn('[Back in stock] Klaviyo svarade ' + response.status +
                  ' för variant ' + variantId + ' (' + variantTitle + ')', body);
              });
              self.showMessage(self.dataset.errorText, false, '');
            })
            .catch(function (err) {
              self.setBusy(false);
              console.warn('[Back in stock] Nätverksfel mot Klaviyo', err);
              self.showMessage(self.dataset.errorText, false, '');
            });
        });
      }

      setBusy(busy) {
        this.classList.toggle('is-loading', !!busy);
        if (!this.button) return;
        this.button.disabled = !!busy;
        this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
      }

      showMessage(text, success, variantTitle) {
        if (!this.message || !this.messageText) return;
        this.messageText.textContent = text || '';
        if (this.messageVariant) {
          var showVariant = !!(success && variantTitle);
          this.messageVariant.textContent = showVariant ? variantTitle : '';
          this.messageVariant.hidden = !showVariant;
        }
        this.message.hidden = false;
        this.message.classList.toggle('bis__status--success', !!success);
        this.message.classList.toggle('bis__status--error', !success);
        this.message.setAttribute('role', success ? 'status' : 'alert');
        if (success && this.fields) this.fields.hidden = true;
        if (typeof this.message.scrollIntoView === 'function') {
          this.message.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }
  );
})();